import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { connect } from 'react-redux';
import getConfig from 'next/config';
import { stub as $t } from '@nbcnews/analytics-framework';

import loadScript from 'lib/loadScript';
import {
  ACCESS_ENABLER_OBJ,
  configTVE,
  getAccessEnablerConfig,
  getResourceID,
  notifyErrorMap,
} from 'lib/tve';

import {
  hideProviderIFrameAction,
  setAuthenticationAction,
  setAuthorizationAction,
  setAccountMetadataAction,
  setTempPassProviderAction,
  showProviderIFrameAction,
} from 'redux/modules/tve';
import { setErrorAction } from 'redux/modules/video/error';

import Notice from 'components/TVE/Notice';
import { ERROR_TYPES } from 'components/TVE/Slate';

const TRACKING_EVENT_NAME = 'tve_auth';

// Connect to store authentication and authorization in global state
const mapStateToProps = ({ router, shared, tve }) => ({
  authenticated: tve.authenticated,
  domain: router.domain,
  hasTempPass: tve.hasTempPass,
  selectedProvider: tve.provider,
  tempPassProviderID: tve.tempPassProviderID,
  vertical: shared.vertical,
});

const mapActionsToProps = (dispatch) => ({
  hideProviderIFrame: () => dispatch(hideProviderIFrameAction()),
  setAuthentication: (payload) => dispatch(setAuthenticationAction(payload)),
  setAuthorization: (payload) => dispatch(setAuthorizationAction(payload)),
  setAccountMetadata: (payload) => dispatch(setAccountMetadataAction(payload)),
  setError: (error) => dispatch(setErrorAction(error)),
  setTempPassProvider: (payload) => dispatch(setTempPassProviderAction(payload)),
  showProviderIFrame: () => dispatch(showProviderIFrameAction()),
});

/**
 * Logs debug messages to the console if available.
 */
const logDebugMsg = (/* ...args */) => {
  // eslint-disable-next-line no-console
  // if (window.console) console.log(...args);
};

/**
 * TVEAuthProvider component handles authentication and authorization flows
 * for TV Everywhere (TVE) providers using Adobe Access Enabler.
 *
 * @param {object} props - Component props.
 * @param {boolean} props.disableBackgroundLogin - If true, disables background login.
 * @param {string} props.domain - The domain key for configuration.
 * @param {object} props.iframeRef - Ref to the iframe wrapper element.
 * @param {Function} props.showProviderIFrame - Function to show the provider iframe.
 * @param {Function} props.hideProviderIFrame - Function to hide the provider iframe.
 * @param {Function} props.setError - Function to set error state.
 * @param {Function} props.setAuthentication - Function to set authentication state.
 * @param {Function} props.setTempPassProvider - Function to set temp pass provider.
 * @param {string} props.selectedProvider - Currently selected provider.
 * @param {Function} props.setAuthorization - Function to set authorization state.
 * @param {Function} props.setAccountMetadata - Function to set account metadata.
 * @param {boolean} props.authenticated - Whether the user is authenticated.
 */
const TVEAuthProvider = (props) => {
  const [error, setError] = useState(null);
  const propsRef = useRef({ ...props });

  useEffect(() => {
    // update ref to the latest props, so that window callbacks always have access to the latest prop values
    propsRef.current = { ...props };
  }, [props]);
  /**
   * The user selected a MVPD which requires an iFrame
   */
  const accessEnablerCreateIFrame = useCallback((width, height) => {
    const { showProviderIFrame, iframeRef } = propsRef.current;

    showProviderIFrame?.();

    const iframeWrap = iframeRef.current;

    // Create the iframe to the specified width and height for the MVPD login page.
    const iframe = iframeWrap.childNodes[0];
    iframe.style.width = `${width}px`;
    iframe.style.height = `${height}px`;

    logDebugMsg('createIframe', iframeRef);
  }, []);

  /**
   * Callback triggered when a MVPD requiring an iFrame authenticates
   */
  const accessEnablerDestroyIFrame = useCallback(() => {
    const { hideProviderIFrame, iframeRef } = propsRef.current;
    hideProviderIFrame?.();

    const iframeWrap = iframeRef.current;
    // Reset frame source
    const iframe = iframeWrap.childNodes[0];
    iframe.src = configTVE.iframeSrc;
  }, []);

  /**
   * Implement this callback to invoke your own custom provider-selection UI
   * @param {array} providers An array of Objects representing the requested MVPDs
   */
  const accessEnablerDisplayProviderDialog = useCallback(() => { }, []);

  /**
   * Callback triggered by initial load of the Access Enabler
   */
  const accessEnablerLoaded = useCallback(() => {
    const {
      domain,
      disableBackgroundLogin,
    } = propsRef.current;

    const {
      [domain]: {
        backgroundLogin,
        backgroundLogout,
        requestorID,
      } = {},
    } = configTVE;

    if (requestorID) {
      const enabler = window[ACCESS_ENABLER_OBJ];
      enabler.bind('errorEvent', 'handleAccessEnablerErrors');
      // Set the requestor ID for the authN check
      enabler.setRequestor(requestorID, null, {
        backgroundLogin: (disableBackgroundLogin ? false : backgroundLogin),
        backgroundLogout,
      });
      // Start the authN flow, which triggers setAuthenticationStatus()
      enabler.checkAuthentication();
    }
  }, [configTVE]);

  /**
   * Handles special errors passed by the Access Enabler (bound by errorEvent)
   * @param {object} error An object with information about the error received
   * @param {string} error.errorId The ID code of the error received
   * @param {string} error.level The severity of the error
   * @param {string} error.message The error message to show to the user
   */
  const accessEnablerError = useCallback((e) => {
    const { setError: setErrorProp } = propsRef.current;

    // http://tve.helpdocsonline.com/error-reporting$adv_err_ref
    if (e.level === 'error') {
      const { errorId } = e;
      if (errorId && Object.keys(notifyErrorMap).includes(errorId)) {
        setError(errorId);
      } else {
        setErrorProp?.(ERROR_TYPES.ERROR);
      }
    }

    logDebugMsg('Error', e);
  }, []);

  /**
   * Callback triggered by accessEnabler.getSelectedProvider()
   * @param {object} provider The information for the selected provider
   * @param {string} provider.AE_State The Access Enabler user state:
   *   'User Authenticated', 'New User'
   * @param {string} provider.MVPD The MVPD ID of the selected provider
   */
  const accessEnablerSelectedProvider = useCallback(({ AE_State: aeState, MVPD = false }) => {
    const { setAuthentication } = propsRef.current;

    switch (aeState) {
      case 'User Authenticated':
        // Dispatch global auth/provider update
        setAuthentication?.({
          authenticated: true,
          provider: MVPD,
        });
        break;
      default:
    }

    logDebugMsg('SelectedProvider', { aeState, MVPD });
  }, []);

  /**
   * Callback for tracking events sent from Adobe Pass,
   * triggered by checkAuthentication(), checkAuthorization(), getAuthorization(),
   * and setSelectedProvider()
   * @param {string} eventType The type of event this represents: authenticationDetection,
   *   authorizationDetection, mvpdSelection
   * @param {Array} data A list of tracking data
   */
  const accessEnablerSendTrackingData = useCallback((eventType, data) => {
    const { setAuthentication } = propsRef.current;

    if (['authenticationDetection', 'authorizationDetection'].includes(eventType)) {
      const [auth] = data;
      if (!auth) {
        setAuthentication?.({ provider: null });
      }
    }

    logDebugMsg('Tracking', { eventType, data });

    // Log tracking event
    $t('track', TRACKING_EVENT_NAME, { action: eventType, data });
  }, []);

  /**
   * Callback triggered by checkAuthentication(), getAuthentication(), and checkAuthorization();
   * @param {number} authed 1 for authenticated or 0 for not authenticated
   * @param {string} error The error to be displayed, if any
   */
  const accessEnablerSetAuthStatus = useCallback((authed, e) => {
    const {
      domain,
      setAuthentication,
      setAuthorization,
      selectedProvider,
    } = propsRef.current;

    // authenticated can be "1" or 1
    if (parseInt(authed, 10) === 1) {
      // Authenticated
      const {
        [domain]: {
          channelTitle, itemTitle, guid,
        } = {},
      } = configTVE;

      const resourceID = getResourceID(channelTitle, itemTitle, guid);

      const enabler = window[ACCESS_ENABLER_OBJ];
      // Confirm the authenticated provider
      enabler.getSelectedProvider();
      // Begin authZ check, triggers setToken() or tokenRequestFailed()
      enabler.getAuthorization(resourceID);
      // Extract metadata details
      enabler.getMetadata('userID');
      enabler.getMetadata('hba_status');

      // Dispatch global authentication state update
      setAuthentication?.({ authenticated: true });
    } else {
      // Not Authenticated. Dispatch global authorization state update
      setAuthorization?.({ token: null });
    }

    logDebugMsg('SetAuthStatus', { authed, e, selectedProvider });
  }, [getResourceID, configTVE]);

  /**
   * Receive the configuration information and MVPD list.
   */
  const accessEnablerSetConfig = useCallback((config) => {
    const { setTempPassProvider } = propsRef.current;

    if (/^<config>/.test(config)) {
      const Parser = new DOMParser();
      const DOM = Parser.parseFromString(config, 'application/xml');
      // Find tempPass MVPDs
      const MVPDs = DOM.querySelectorAll('config > requestor > mvpds > mvpd > tempPass');
      for (let m = 0; m < MVPDs.length; m += 1) {
        const id = MVPDs[m].parentNode.querySelector('id');
        // Ignore flexibleTempPass
        if (id?.textContent && id.textContent.indexOf('flex') === -1) {
          setTempPassProvider?.(id.textContent);
          break;
        }
      }
    }
  }, []);

  /**
   * Callback triggered by checkAuthorization() or getAuthorization() on authZ success
   * @param {string} resourceID The resource ID for which authorization was obtained
   * @param {string} token The short media token
   */
  const accessEnablerSetToken = useCallback((resourceID, token) => {
    const { setAuthorization, selectedProvider } = propsRef.current;

    // Dispatch global authorization update
    setAuthorization?.({ token });

    logDebugMsg('SetToken', { resourceID, token, selectedProvider });
  }, []);

  /**
   * Callback triggered by the Access Enabler that delivers the metadata requested
   * via a getMetadata() call.
   * @param {string} key The key of the metadata for which the request was made
   * @param {bool} encrypted A flag signifying whether the "value" is encrypted or not
   * @param {object} data
   */
  const accessEnablerSetMetadataStatus = useCallback((...args) => {
    const { setAccountMetadata } = propsRef.current;

    logDebugMsg('SetMetaData', args);

    const key = args[0];
    if (!key) {
      return;
    }

    setAccountMetadata({ [key]: args[2] ?? '' });
  }, []);

  /**
   * Callback triggered by checkAuthorization() or getAuthorization() on authZ failure
   * @param {string} resourceID The resource ID for which authorization was requested
   * @param {string} errorCode The Adobe error code type
   * @param {string} errorMessage The full error message for user display
   */
  const accessEnablerTokenRequestFailed = useCallback((resourceID, errorCode, errorMessage) => {
    const { authenticated } = propsRef.current;

    switch (errorCode) {
      case 'User Not Authenticated Error':
      case 'User Not Authorized Error':
        if (authenticated) {
          window[ACCESS_ENABLER_OBJ].logout();
        }
        break;

      default:
    }

    logDebugMsg('TokenRequestFailed', { resourceID, errorCode, errorMessage });
  }, []);

  /**
   * Allow the user to clear the error added above.
   */
  const handleClearError = useCallback(() => {
    setError(null);
  }, []);

  useEffect(() => {
    // Argument of 'false' to return staging configs
    const getAEConfig = getAccessEnablerConfig(process.env.NODE_ENV === 'production');

    const {
      publicRuntimeConfig: {
        ACCESS_ENABLER_SCRIPT_URL,
      },
    } = getConfig();

    if (typeof window[ACCESS_ENABLER_OBJ] === 'undefined') {
      // Load AccessEnabler script
      loadScript(ACCESS_ENABLER_SCRIPT_URL || getAEConfig('script'))
        .then(() => {
          // Set up API
          window[ACCESS_ENABLER_OBJ] = new window.Adobe.AccessEnabler(
            getAEConfig('softwareStatement'),
          );

          // AccessEnabler callback methods
          window.createIFrame = accessEnablerCreateIFrame;
          window.destroyIFrame = accessEnablerDestroyIFrame;
          window.displayProviderDialog = accessEnablerDisplayProviderDialog;
          window.entitlementLoaded = accessEnablerLoaded;
          window.handleAccessEnablerErrors = accessEnablerError;
          window.selectedProvider = accessEnablerSelectedProvider;
          window.sendTrackingData = accessEnablerSendTrackingData;
          window.setAuthenticationStatus = accessEnablerSetAuthStatus;
          window.setConfig = accessEnablerSetConfig;
          window.setToken = accessEnablerSetToken;
          window.setMetadataStatus = accessEnablerSetMetadataStatus;
          window.tokenRequestFailed = accessEnablerTokenRequestFailed;
        });

      // Register tracking event
      $t('register', [TRACKING_EVENT_NAME]);
    }
  }, [
    accessEnablerCreateIFrame,
    accessEnablerDestroyIFrame,
    accessEnablerDisplayProviderDialog,
    accessEnablerError,
    accessEnablerLoaded,
    accessEnablerSendTrackingData,
    accessEnablerSetAuthStatus,
    accessEnablerSetConfig,
    accessEnablerSetMetadataStatus,
    accessEnablerSelectedProvider,
    accessEnablerSetToken,
    accessEnablerTokenRequestFailed,
  ]);

  return (
    <>
      {error && (
        <Notice
          error={error}
          onClick={handleClearError}
        />
      )}
    </>
  );
};

export default connect(mapStateToProps, mapActionsToProps)(TVEAuthProvider);
